/**
 * Customer Authentication Store - Zustand + Supabase Implementation
 *
 * Migrated from AWS Cognito to Supabase Auth.
 * This store manages all authentication state and operations for the customer app.
 *
 * Key Features:
 * - Supabase Auth integration via @rsvd/supabase-client
 * - Automatic state persistence via Zustand persist middleware
 * - Type-safe error handling (Error | null, not any)
 * - Real-time auth state synchronization
 * - User profile management from PostgreSQL
 *
 * @module stores/authStore
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User as SupabaseUser, Session } from '@supabase/supabase-js'
import type { UserProfile } from '@rsvd/supabase-client'
import type { SocialProvider } from '../types/auth'
import { supabase } from '../lib/supabase'
import { getUserProfile, updateUserProfile } from '@rsvd/supabase-client'
import toast from 'react-hot-toast'

/**
 * Registration Data Interface
 */
interface RegisterData {
  email: string
  password: string
  fullName: string
  phoneNumber: string
}

/**
 * Authentication State and Actions
 */
interface AuthState {
  // State
  user: SupabaseUser | null
  session: Session | null
  profile: UserProfile | null
  isLoading: boolean
  isAuthenticated: boolean
  error: Error | null
  _hasHydrated: boolean  // CRITICAL: Tracks Zustand persist hydration completion for E2E tests

  // Authentication Methods
  login: (email: string, password: string) => Promise<boolean>
  register: (data: RegisterData) => Promise<boolean>
  logout: () => Promise<void>
  loginWithSocial: (provider: SocialProvider) => Promise<boolean>
  handleOAuthCallback: () => Promise<boolean>

  // Profile Methods
  refreshProfile: () => Promise<void>
  updateProfile: (updates: Partial<Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>>) => Promise<boolean>

  // Email Verification Methods
  resendVerification: (email?: string) => Promise<boolean>
  // @deprecated - OTP verification replaced with magic link flow
  // verifyOtp: (email: string, token: string) => Promise<boolean>

  // Password Reset Methods
  forgotPassword: (email: string) => Promise<boolean>
  verifyPasswordResetOtp: (email: string, token: string) => Promise<boolean>
  resetPassword: (newPassword: string) => Promise<boolean>

  // Internal Methods
  initializeAuth: () => Promise<void>
}

/**
 * Normalize error messages for user-friendly display
 */
function normalizeError(error: string | undefined, fallback: string): string {
  if (!error) return fallback

  // Supabase-specific error handling
  if (error.includes('Invalid login credentials')) return 'Invalid email or password'
  if (error.includes('Email not confirmed')) return 'Please verify your email before logging in'
  if (error.includes('User already registered')) return 'An account with this email already exists'

  return error || fallback
}

/**
 * Customer Authentication Store
 *
 * Manages authentication state using Supabase Auth and PostgreSQL user profiles.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial State
      user: null,
      session: null,
      profile: null,
      isLoading: true,  // CRITICAL: Must be true initially so ProtectedRoute waits for initializeAuth
      isAuthenticated: false,
      error: null,
      _hasHydrated: false,  // CRITICAL: Must be false initially, set to true by onRehydrateStorage

      /**
       * Login with email and password
       */
      login: async (email: string, password: string): Promise<boolean> => {
        console.warn('[AuthStore] Login called with:', { email });
        set({ isLoading: true, error: null })

        try {
          console.warn('[AuthStore] Calling signInWithPassword...');
          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
          })

          if (error) {
            console.error('[AuthStore] Login failed:', error);
            set({ error: new Error(error.message), isLoading: false })
            toast.error(normalizeError(error.message, 'Login failed'))
            return false
          }

          console.warn('[AuthStore] Auth success, user:', data.user?.id);
          console.warn('[AuthStore] Fetching profile...');

          // Fetch user profile from database
          const profile = await getUserProfile(supabase, data.user.id)
          console.warn('[AuthStore] Profile result:', profile ? 'found' : 'null');

          set({
            user: data.user,
            session: data.session,
            profile,
            isAuthenticated: true,
            isLoading: false
          })

          console.warn('[AuthStore] State set, isAuthenticated=true');
          toast.success('Welcome back!')
          console.warn('[AuthStore] Returning true');
          return true
        } catch (error) {
          console.error('[AuthStore] Exception caught in login:', error);
          const errorObj = error instanceof Error ? error : new Error('Login failed. Please try again.')
          set({ error: errorObj, isLoading: false })
          toast.error('Login failed. Please try again.')
          return false
        }
      },

      /**
       * Register new customer account
       */
      register: async (data: RegisterData): Promise<boolean> => {
        console.log('[AuthStore] Register called with:', { email: data.email });
        set({ isLoading: true, error: null })

        try {
          // Determine redirect URL based on environment
          const redirectUrl = `${window.location.origin}/auth/callback`
          console.log('[AuthStore] Email verification redirect URL:', redirectUrl)

          const { error } = await supabase.auth.signUp({
            email: data.email,
            password: data.password,
            options: {
              emailRedirectTo: redirectUrl,
              data: {
                full_name: data.fullName,
                phone_number: data.phoneNumber,
                user_type: 'customer'
              }
            }
          })

          if (error) {
            console.error('[AuthStore] Registration failed:', error);
            set({ error: new Error(error.message), isLoading: false })
            toast.error(normalizeError(error.message, 'Registration failed'))
            return false
          }

          console.log('[AuthStore] Registration success - magic link sent');
          set({ isLoading: false })
          toast.success('Account created! Check your email for a confirmation link.')
          return true
        } catch (error) {
          console.error('[AuthStore] Exception caught in register:', error);
          const errorObj = error instanceof Error ? error : new Error('Registration failed. Please try again.')
          set({ error: errorObj, isLoading: false })
          toast.error('Registration failed. Please try again.')
          return false
        }
      },

      /**
       * Logout and clear session
       */
      logout: async (): Promise<void> => {
        console.log('[AuthStore] Logout called');
        try {
          await supabase.auth.signOut()
          set({
            user: null,
            session: null,
            profile: null,
            isAuthenticated: false,
            error: null
          })
          toast.success('Logged out successfully')
        } catch (error) {
          console.error('[AuthStore] Logout error:', error)
          // Clear local state even if API call fails
          set({
            user: null,
            session: null,
            profile: null,
            isAuthenticated: false
          })
        }
      },

      /**
       * Login with social OAuth provider (Google or Facebook)
       *
       * Initiates OAuth flow via Supabase Auth. User will be redirected to provider's
       * login page, then back to /auth/callback with session tokens.
       */
      loginWithSocial: async (provider: SocialProvider): Promise<boolean> => {
        console.log('[AuthStore] Social login initiated for:', provider)
        set({ isLoading: true, error: null })

        try {
          const { data, error } = await supabase.auth.signInWithOAuth({
            provider: provider,
            options: {
              redirectTo: `${window.location.origin}/auth/callback`,
              scopes: provider === 'google' ? 'openid email profile' : 'email public_profile',
              queryParams: provider === 'google' ? { prompt: 'select_account' } : undefined
            }
          })

          if (error) {
            console.error('[AuthStore] Social login failed:', error)
            set({ error: new Error(error.message), isLoading: false })
            toast.error(normalizeError(error.message, 'Social login failed'))
            return false
          }

          console.log('[AuthStore] OAuth redirect initiated successfully')
          // User will be redirected to OAuth provider - don't set isLoading to false
          // The callback page will handle session state
          return true
        } catch (error) {
          console.error('[AuthStore] Exception in loginWithSocial:', error)
          const errorObj = error instanceof Error ? error : new Error('Social login failed')
          set({ error: errorObj, isLoading: false })
          toast.error('Social login failed. Please try again.')
          return false
        }
      },

      /**
       * Handle OAuth callback after provider authentication
       *
       * Called from /auth/callback page after user completes OAuth flow.
       * Extracts session from URL, authenticates user, and creates profile if new.
       */
      handleOAuthCallback: async (): Promise<boolean> => {
        console.log('[AuthStore] Handling OAuth callback')
        set({ isLoading: true, error: null })

        try {
          // Get session from URL hash/query params (Supabase handles PKCE validation)
          const { data: { session }, error } = await supabase.auth.getSession()

          if (error) {
            console.error('[AuthStore] OAuth callback error:', error)
            set({ error: new Error(error.message), isLoading: false })
            toast.error(normalizeError(error.message, 'Authentication failed'))
            return false
          }

          if (!session?.user) {
            console.error('[AuthStore] OAuth callback: No session found')
            set({ isLoading: false })
            toast.error('Authentication failed. Please try again.')
            return false
          }

          console.log('[AuthStore] OAuth session established for user:', session.user.id)

          // Fetch or create user profile
          let profile = await getUserProfile(supabase, session.user.id)

          // If profile doesn't exist, create it from OAuth metadata
          if (!profile) {
            console.log('[AuthStore] Creating new profile for OAuth user')
            const metadata = session.user.user_metadata

            // Use Supabase's update method to create profile via database trigger
            const { error: updateError } = await supabase.auth.updateUser({
              data: {
                full_name: metadata.full_name || metadata.name || '',
                user_type: 'customer'
              }
            })

            if (updateError) {
              console.error('[AuthStore] Failed to update user metadata:', updateError)
            }

            // Fetch profile again after creation
            profile = await getUserProfile(supabase, session.user.id)
          }

          set({
            user: session.user,
            session,
            profile,
            isAuthenticated: true,
            isLoading: false
          })

          console.log('[AuthStore] OAuth authentication completed successfully')
          toast.success('Signed in successfully!')
          return true
        } catch (error) {
          console.error('[AuthStore] Exception in handleOAuthCallback:', error)
          const errorObj = error instanceof Error ? error : new Error('Authentication failed')
          set({ error: errorObj, isLoading: false })
          toast.error('Authentication failed. Please try again.')
          return false
        }
      },

      /**
       * Refresh user profile from database
       */
      refreshProfile: async (): Promise<void> => {
        console.log('[AuthStore] Refresh profile called');
        const { user } = get()
        if (user) {
          try {
            const profile = await getUserProfile(supabase, user.id)
            set({ profile })
            console.log('[AuthStore] Profile refreshed successfully');
          } catch (error) {
            console.error('[AuthStore] Failed to refresh profile:', error)
          }
        }
      },

      /**
       * Update user profile in database
       *
       * Updates the user profile with the provided fields and syncs the local state.
       * Used by ProfileSetup wizard and profile editing pages.
       *
       * @param updates - Partial profile updates to apply
       * @returns true if update succeeded, false otherwise
       */
      updateProfile: async (updates): Promise<boolean> => {
        console.log('[AuthStore] Update profile called with:', updates);
        const { user } = get()

        if (!user) {
          console.error('[AuthStore] Cannot update profile: No user logged in');
          return false
        }

        set({ isLoading: true, error: null })

        try {
          const updatedProfile = await updateUserProfile(supabase, user.id, updates)

          if (updatedProfile) {
            set({ profile: updatedProfile, isLoading: false })
            console.log('[AuthStore] Profile updated successfully');
            return true
          }

          console.error('[AuthStore] Profile update returned null');
          set({ isLoading: false })
          return false
        } catch (error) {
          console.error('[AuthStore] Failed to update profile:', error);
          const errorObj = error instanceof Error ? error : new Error('Profile update failed')
          set({ error: errorObj, isLoading: false })
          return false
        }
      },

      /**
       * Resend verification email with magic link
       *
       * Uses the registered email from localStorage or provided email parameter.
       * Called when user clicks "Resend verification email" button.
       * Sends a magic link instead of OTP code.
       */
      resendVerification: async (email?: string): Promise<boolean> => {
        const targetEmail = email || localStorage.getItem('customer_registeredEmail')

        if (!targetEmail) {
          console.error('[AuthStore] Cannot resend verification: No email found')
          toast.error('No email address found. Please register again.')
          return false
        }

        console.log('[AuthStore] Resending verification magic link to:', targetEmail)
        set({ isLoading: true, error: null })

        try {
          const redirectUrl = `${window.location.origin}/auth/callback`

          const { error } = await supabase.auth.resend({
            type: 'signup',
            email: targetEmail,
            options: {
              emailRedirectTo: redirectUrl
            }
          })

          if (error) {
            console.error('[AuthStore] Resend verification failed:', error)
            set({ error: new Error(error.message), isLoading: false })
            toast.error(normalizeError(error.message, 'Failed to resend verification email'))
            return false
          }

          console.log('[AuthStore] Verification magic link resent successfully')
          set({ isLoading: false })
          toast.success('Confirmation link sent! Check your inbox.')
          return true
        } catch (error) {
          console.error('[AuthStore] Exception in resendVerification:', error)
          const errorObj = error instanceof Error ? error : new Error('Failed to resend verification email')
          set({ error: errorObj, isLoading: false })
          toast.error('Failed to resend verification email. Please try again.')
          return false
        }
      },

      /**
       * @deprecated - OTP verification replaced with magic link flow
       * Use handleOAuthCallback() instead, which processes magic link redirects
       *
       * Verify email with OTP code
       * Called when user enters the 6-digit verification code from their email.
       * On success, completes the signup flow and authenticates the user.
       */
      /* DEPRECATED - Keeping for reference, remove after migration complete
      verifyOtp: async (email: string, token: string): Promise<boolean> => {
        console.log('[AuthStore] Verifying OTP for:', email)
        set({ isLoading: true, error: null })

        try {
          const { data, error } = await supabase.auth.verifyOtp({
            email,
            token,
            type: 'signup'
          })

          if (error) {
            console.error('[AuthStore] OTP verification failed:', error)
            set({ error: new Error(error.message), isLoading: false })
            toast.error(normalizeError(error.message, 'Verification failed'))
            return false
          }

          if (data.user && data.session) {
            console.log('[AuthStore] OTP verified, fetching profile...')

            // Fetch user profile from database
            const profile = await getUserProfile(supabase, data.user.id)

            set({
              user: data.user,
              session: data.session,
              profile,
              isAuthenticated: true,
              isLoading: false
            })

            // Clear stored email
            localStorage.removeItem('customer_registeredEmail')

            console.log('[AuthStore] Email verified and authenticated successfully')
            toast.success('Email verified successfully!')
            return true
          }

          console.log('[AuthStore] OTP verified but no session returned')
          set({ isLoading: false })
          return false
        } catch (error) {
          console.error('[AuthStore] Exception in verifyOtp:', error)
          const errorObj = error instanceof Error ? error : new Error('Verification failed')
          set({ error: errorObj, isLoading: false })
          toast.error('Verification failed. Please try again.')
          return false
        }
      },
      */

      /**
       * Request password reset email
       *
       * Sends a password reset email via Supabase Auth.
       * User will receive an OTP code to verify before resetting password.
       */
      forgotPassword: async (email: string): Promise<boolean> => {
        console.log('[AuthStore] Forgot password called for:', email)
        set({ isLoading: true, error: null })

        try {
          const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/forgot-password/reset`
          })

          if (error) {
            console.error('[AuthStore] Forgot password failed:', error)
            set({ error: new Error(error.message), isLoading: false })
            toast.error(normalizeError(error.message, 'Failed to send reset email'))
            return false
          }

          console.log('[AuthStore] Password reset email sent successfully')
          set({ isLoading: false })
          toast.success('Password reset code sent! Check your email.')
          return true
        } catch (error) {
          console.error('[AuthStore] Exception in forgotPassword:', error)
          const errorObj = error instanceof Error ? error : new Error('Failed to send reset email')
          set({ error: errorObj, isLoading: false })
          toast.error('Failed to send reset email. Please try again.')
          return false
        }
      },

      /**
       * Verify password reset OTP code
       *
       * Called when user enters the OTP from their password reset email.
       * On success, user is authenticated and can proceed to reset password.
       */
      verifyPasswordResetOtp: async (email: string, token: string): Promise<boolean> => {
        console.log('[AuthStore] Verifying password reset OTP for:', email)
        set({ isLoading: true, error: null })

        try {
          const { data, error } = await supabase.auth.verifyOtp({
            email,
            token,
            type: 'recovery'
          })

          if (error) {
            console.error('[AuthStore] Password reset OTP verification failed:', error)
            set({ error: new Error(error.message), isLoading: false })
            toast.error(normalizeError(error.message, 'Invalid or expired code'))
            return false
          }

          if (data.user && data.session) {
            console.log('[AuthStore] Password reset OTP verified, user authenticated')
            set({
              user: data.user,
              session: data.session,
              isAuthenticated: true,
              isLoading: false
            })
            return true
          }

          console.log('[AuthStore] Password reset OTP verified but no session returned')
          set({ isLoading: false })
          return false
        } catch (error) {
          console.error('[AuthStore] Exception in verifyPasswordResetOtp:', error)
          const errorObj = error instanceof Error ? error : new Error('Verification failed')
          set({ error: errorObj, isLoading: false })
          toast.error('Verification failed. Please try again.')
          return false
        }
      },

      /**
       * Reset password with new password
       *
       * Called after user has verified their OTP and is authenticated.
       * Updates the user's password via Supabase Auth.
       */
      resetPassword: async (newPassword: string): Promise<boolean> => {
        console.log('[AuthStore] Resetting password')
        set({ isLoading: true, error: null })

        try {
          const { error } = await supabase.auth.updateUser({
            password: newPassword
          })

          if (error) {
            console.error('[AuthStore] Password reset failed:', error)
            set({ error: new Error(error.message), isLoading: false })
            toast.error(normalizeError(error.message, 'Failed to reset password'))
            return false
          }

          console.log('[AuthStore] Password reset successfully')
          set({ isLoading: false })
          toast.success('Password reset successfully!')
          return true
        } catch (error) {
          console.error('[AuthStore] Exception in resetPassword:', error)
          const errorObj = error instanceof Error ? error : new Error('Failed to reset password')
          set({ error: errorObj, isLoading: false })
          toast.error('Failed to reset password. Please try again.')
          return false
        }
      },

      /**
       * Initialize authentication state on app mount
       */
      initializeAuth: async (): Promise<void> => {
        console.log('[AuthStore] Initialize auth called');
        try {
          const { data: { session } } = await supabase.auth.getSession()

          if (session?.user) {
            console.log('[AuthStore] Session found, fetching profile...');
            const profile = await getUserProfile(supabase, session.user.id)
            set({
              user: session.user,
              session,
              profile,
              isAuthenticated: true,
              isLoading: false
            })
            console.log('[AuthStore] Successfully restored user session');
          } else {
            console.log('[AuthStore] No session found, starting with unauthenticated state');
            set({ isLoading: false })
          }
        } catch (error) {
          console.error('[AuthStore] Failed to initialize auth:', error)
          // Clear potentially corrupted session
          set({
            user: null,
            session: null,
            profile: null,
            isAuthenticated: false,
            isLoading: false
          })
        }
      }
    }),
    {
      name: 'rsvd-customer-auth',
      partialize: (_state) => ({
        // Don't persist anything - Supabase handles session storage
      }),
      onRehydrateStorage: () => {
        console.log('[AuthStore] Starting hydration from localStorage...');
        return (state, error) => {
          // CRITICAL: Use queueMicrotask to defer setState until after store variable
          // is fully assigned. This prevents TDZ (Temporal Dead Zone) errors in bundled
          // code where useAuthStore might be accessed before initialization completes.
          queueMicrotask(() => {
            if (error) {
              console.error('[AuthStore] Hydration error:', error);
            }
            // Always mark hydration as complete, even on error
            // Otherwise ProtectedRoute waits forever on _hasHydrated
            useAuthStore.setState({ _hasHydrated: true });
            console.log('[AuthStore] Hydration complete');
          });
        };
      }
    }
  )
)

/**
 * Listen for auth state changes from Supabase
 *
 * This ensures the store stays in sync with Supabase Auth state changes
 * (e.g., token refresh, logout from another tab, etc.)
 *
 * IMPORTANT: Do NOT call getUserProfile here - it causes duplicate calls
 * that hang the second request. Profile fetching is handled by:
 * - login() for new sign-ins
 * - initializeAuth() for session restoration on page refresh
 *
 * DEBOUNCE: Prevents infinite SIGNED_OUT loop when no session exists.
 * Supabase may fire multiple SIGNED_OUT events rapidly on page load.
 *
 * MEMORY LEAK FIX: Store subscription for cleanup on app unmount.
 * Without cleanup, listeners accumulate during HMR and navigation.
 */
let lastSignedOutTime = 0;
const SIGNED_OUT_DEBOUNCE_MS = 1000; // 1 second debounce

// Store subscription for cleanup
const authStateSubscription = supabase.auth.onAuthStateChange(async (event, session) => {
  console.warn('[AuthStore] Auth state change:', event);

  if (event === 'SIGNED_OUT') {
    // Debounce: Skip if already signed out recently
    const now = Date.now();
    if (now - lastSignedOutTime < SIGNED_OUT_DEBOUNCE_MS) {
      console.log('[AuthStore] Debounced duplicate SIGNED_OUT event');
      return;
    }
    lastSignedOutTime = now;

    // Skip if already signed out (prevent redundant state updates)
    const { isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated) {
      console.log('[AuthStore] Already signed out, skipping state update');
      return;
    }

    // Clear all state on sign out
    useAuthStore.setState({
      user: null,
      session: null,
      profile: null,
      isAuthenticated: false
    })
  } else if (event === 'TOKEN_REFRESHED' && session) {
    // Update session on token refresh (profile doesn't change)
    console.warn('[AuthStore] Token refreshed, updating session');
    useAuthStore.setState({
      session
    })
  }
  // SIGNED_IN is handled by login() function
  // INITIAL_SESSION is handled by initializeAuth()
})

/**
 * Cleanup auth store subscriptions
 *
 * Call this on app unmount to prevent memory leaks.
 * Usage: useEffect(() => () => cleanupAuthStore(), [])
 */
export const cleanupAuthStore = (): void => {
  if (authStateSubscription?.data?.subscription) {
    authStateSubscription.data.subscription.unsubscribe()
    console.log('[AuthStore] Auth state subscription cleaned up')
  }
}
